import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { formatDistance } from "tsi-common-react/src/apps/retail/helpers";
import { Link } from "tsi-common-react/src/components/Link/index";
import {
    RetailStoreEventCTAType,
    RetailStoreEventType,
} from "tsi-common-react/src/constants";
import { RetailStore } from "tsi-common-react/src/models/location";
import { isoPostcode, isoStateA2 } from "tsi-common-react/src/models/nominals";
import { trackRetailStoreEvent } from "tsi-common-react/src/utils/analytics";
import {
    DistanceUnit,
    getDistanceUnit,
} from "tsi-common-react/src/utils/distance";
import { formatPhoneNumber } from "tsi-common-react/src/utils/format";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { HolidayHoursSummary } from "../HolidayHoursSummary";
import { CMSFlagshipStorePage } from "../models";
import { getTodayHours } from "../utils";

type IProps = Immutable<{
    store: RetailStore;
    cmsProps: CMSFlagshipStorePage;
    isFocused: boolean;
    includeLocationInfo: boolean;
}> & {
    onActivateMarker: () => void;
};

export const FlagshipNearestStoresTile = (props: IProps): React.ReactNode => {
    const staticURL = useStaticURL();
    // Monitor data layer events when clicking on store location details like directions, phone numbers.
    const onClickLocationInfo = (
        ctaType: RetailStoreEventCTAType,
        textContent: string | null,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation();
        const element = event.currentTarget;
        const linkText = textContent || element.textContent?.trim();
        const linkUrl = element.getAttribute("href");
        trackRetailStoreEvent(
            RetailStoreEventType.TEMPUR_STORE,
            props.store,
            ctaType,
            linkText,
            linkUrl,
        );
    };

    const buildLocationInfo = () => {
        if (!props.includeLocationInfo) {
            return;
        }
        const phoneInUSFormat = props.store.phone
            ? formatPhoneNumber(props.store.phone)
            : "";
        // encode destination address for Directions link
        const destinationAddress = encodeURI(
            [
                props.store.name,
                props.store.address,
                props.store.address2,
                props.store.city,
                props.store.state,
                props.store.postal,
            ].join(" "),
        );
        return (
            <div className="loc__info">
                <div className="loc__contact">
                    <div className="loc__addr">
                        <span className="loc__addr1">
                            {props.store.address}, {props.store.address2}
                        </span>
                        <br />
                        <span className="loc__city">{props.store.city}</span>
                        ,&nbsp;
                        <span className="loc__state">
                            {isoStateA2.unwrap(props.store.state)}
                        </span>
                        &nbsp;
                        <span className="loc__postal">
                            {isoPostcode.unwrap(props.store.postal)}
                        </span>
                    </div>
                    <br />
                    {phoneInUSFormat && (
                        <a
                            className="loc__phone"
                            href={`tel:${props.store.phone}`}
                            onClick={(e) =>
                                onClickLocationInfo(
                                    RetailStoreEventCTAType.CALL,
                                    null,
                                    e,
                                )
                            }
                        >
                            {phoneInUSFormat}
                        </a>
                    )}
                    <div className="loc__hours">
                        {t`Today:`} {getTodayHours(props.cmsProps)}
                    </div>
                    <HolidayHoursSummary
                        className="loc__holiday-hours"
                        lines={props.cmsProps.upcoming_holiday_hours_summary}
                    />
                </div>
                <div className="loc__links">
                    <Link
                        className="loc__link loc__link--store-url"
                        href={props.cmsProps.meta.html_url}
                        title={t`Tempur-Pedic Store`}
                    >
                        <img
                            src={`${staticURL}img/retail-locator/icon-info.svg`}
                            loading="lazy"
                            decoding="async"
                        />
                    </Link>
                    <a
                        className="loc__link"
                        href={`https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) =>
                            onClickLocationInfo(
                                RetailStoreEventCTAType.MAP_DIRECTIONS,
                                t`Get icon directions`,
                                e,
                            )
                        }
                    >
                        <img
                            src={`${staticURL}img/locator/icon-directions.svg`}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
            </div>
        );
    };

    const onClickStoreTile = () => {
        props.onActivateMarker();
        trackRetailStoreEvent(RetailStoreEventType.TEMPUR_STORE, props.store);
    };

    const headerStyles: React.CSSProperties = {
        backgroundImage: props.cmsProps.storefront_thumbnail
            ? `url(${props.cmsProps.storefront_thumbnail.url})`
            : undefined,
    };
    const tileClasses = classNames({
        "locations__list-item": true,
        "list-focus": props.isFocused,
    });
    const headerClasses = classNames({
        "loc__header": true,
        "loc__header--hidden": props.includeLocationInfo,
    });
    return (
        <li onClick={onClickStoreTile} className={tileClasses}>
            <Link
                href={props.cmsProps.meta.html_url}
                className={headerClasses}
                style={headerStyles}
            >
                <div className="loc__headlines">
                    <h4 className="loc__headline">
                        {props.store.city},{" "}
                        {isoStateA2.unwrap(props.store.state)}
                    </h4>
                    <h5 className="loc__subheadline">
                        {props.cmsProps.shopping_district}
                    </h5>
                </div>
            </Link>
            <div className="loc__milesaway">
                <span className="loc__miles">
                    <span className="loc__distance">
                        {props.store.distance &&
                            formatDistance(props.store.distance)}
                    </span>
                    <br />
                    <span className="loc__length">
                        {getDistanceUnit() === DistanceUnit.KILOMETERS
                            ? t`km`
                            : t`miles`}
                    </span>
                </span>
            </div>
            {buildLocationInfo()}
        </li>
    );
};
